import React, { useState, useEffect } from 'react';
import { useCreatingNewUserAtom } from '../../atoms/users/useCreatingNewUserAtom';
import { registerUser } from '../../api/authenticateAPIs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';

import AllTeamsMultiselect from '../../components/shared/AllTeamsMultiselect';
import RoleSelectBox from '../../components/shared/RoleSelectBox';
import PasswordGenerateButton from '../../components/shared/PasswordGenerateButton';
import useAuth from "../../hooks/useAuth";

import '../../css/pages/create_user.css';

const CreateUser = () => {
  const { auth } = useAuth();
  const { showNotification } = useNotification();

  const navigate = useNavigate();
  const [t] = useTranslation('global');
  const [, setCreatingNewUser] = useCreatingNewUserAtom();

  const [isLoading, setIsLoading] = useState(false);
  const [pwdMatch, setPwdMatch] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [formData, setFormData] = useState({
    newUserName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    roleId : '',
    teamIds: []
  });

  const handleChangeEmail = (e) => {
    const { name, value } = e.target;
    let noDiacritics = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    setFormData({ ...formData, [name]: noDiacritics });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Validate password in real-time
    if (name === 'password') {
      const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+=<>,.?/|\-])[a-zA-Z0-9!@#$%^&*()_+=<>,.?/|\-]{8,}$/;
      setPasswordError(!passwordRegex.test(value));
    }
  };


  // Vygenerované heslo (spĺňa pravidlá) do oboch políčok + zobraziť, nech ho admin vidí
  const handleGeneratedPassword = (pwd) => {
    setFormData(prev => ({ ...prev, password: pwd, confirmPassword: pwd }));
    setPasswordError(false);
    setPwdMatch('');
    setShowPassword(true);
    setShowConfirmPassword(true);
  };

  const handleTeamChange = (event) => {
    const {
        target: { value },
    } = event;
    setFormData({
        ...formData,
        teamIds: typeof value === 'string' ? value.split(',') : value,
    });
};

  const handleGoBack = () => {
    setCreatingNewUser(false);
    navigate('/Users');
  };

  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+=<>,.?/|\-])[a-zA-Z0-9!@#$%^&*()_+=<>,.?/|\-]{8,}$/;

    // Validate First Name
    if (!formData.newUserName || formData.newUserName.trim() === '') {
      showNotification({ message: t('newUserName') + ' ' + t('isRequired'), type: NotificationType.Error });
      return false;
    }

    // Validate Last Name
    if (!formData.lastName || formData.lastName.trim() === '') {
      showNotification({ message: t('lastName') + ' ' + t('isRequired'), type: NotificationType.Error });
      return false;
    }

    // Validate Email
    if (!formData.email || formData.email.trim() === '') {
      showNotification({ message: t('email') + ' ' + t('isRequired'), type: NotificationType.Error });
      return false;
    }

    if (!emailRegex.test(formData.email)) {
      showNotification({ message: t('invalidEmail'), type: NotificationType.Error });
      return false;
    }

    // Validate Password
    if (!formData.password || formData.password.trim() === '') {
      showNotification({ message: t('password') + ' ' + t('isRequired'), type: NotificationType.Error });
      return false;
    }

    if (formData.password.length < 8) {
      showNotification({ message: t('pwdRule'), type: NotificationType.Error });
      return false;
    }

    if (!passwordRegex.test(formData.password)) {
      showNotification({ message: t('pwdRule'), type: NotificationType.Error });
      return false;
    }

    // Validate Confirm Password
    if (!formData.confirmPassword || formData.confirmPassword.trim() === '') {
      showNotification({ message: t('confirmPassword') + ' ' + t('isRequired'), type: NotificationType.Error });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      showNotification({ message: t('passwordsDontMatch'), type: NotificationType.Error });
      return false;
    }

    // Validate Role
    if (formData.roleId === "" || !formData.roleId) {
      showNotification({ message: t('selectRoleError'), type: NotificationType.Error });
      return false;
    }

    // Validate Teams
    if (!formData.teamIds || formData.teamIds.length === 0) {
      showNotification({ message: t('selectTeamsRequired'), type: NotificationType.Error });
      return false;
    }

    return true;
  };

  const handleConfirmPassword = () => {
    if (formData.password !== formData.confirmPassword) {
      setPwdMatch(t('passwordsDontMatch'));
    } else {
      setPwdMatch('');
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }
    registerUser(
      formData.newUserName,
      formData.lastName,
      formData.email,
      formData.password,
      formData.roleId,
      formData.teamIds
    )
    .then(() => {
      setIsLoading(false);
      showNotification({ message: t('userCreatedSuccess'), type: NotificationType.Success });
      navigate('/Users');
    })
      .catch((resp) => {
        setIsLoading(false);
        
        // let errorMessage = [];
        // for (let i = 0; i < resp.response.data.message?.length; i++) {    
        //   errorMessage += i + 1 + ". " + resp.response.data.message[i] + " ";
        // }
        showNotification({ message: resp.response.data.message, type: NotificationType.Error });
      });
  };


  useEffect(() => {
    if (auth.uri === 1) {
      return;
    } else {
      navigate('/Unauthorized');
    }
  }, []);

  return (
    <>
    {auth.uri === 1  && 
        <div className="user_form page-wrap">
          <button className="return-button return-button-top" onClick={handleGoBack}>
            <span className="material-symbols-outlined">arrow_back</span>{t('return')}
          </button>
          <div className="header-title">
            <h1>{t('newUser')}</h1>
          </div>
          <form onSubmit={handleSubmit} autoComplete="off" className="input-form">

            <div className="create-user-input-section">
              <div className="input-line">
                <label htmlFor="newUserName">{t('newUserName')}</label>
                <input
                  type="text"
                  id="newUserName"
                  name="newUserName"
                  value={formData.newUserName}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
              </div>
              <div className="input-line">
                <label htmlFor="lastName">{t('lastName')}</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="create-user-input-section">
              <div className="input-line">
                <label htmlFor="email">{t('email')}</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChangeEmail}
                  autoComplete="new-password"
                />
              </div>

              <div className='input-line'>
                <div className="selectbox-section">
                  <RoleSelectBox selectValue={formData.roleId} onChange={handleChange}/>
                </div>
             </div>
            </div>

            <div className="create-user-input-section">
              <div className="input-line">
                <div className="pwd-label-row">
                  <label htmlFor="password">{t('password')}</label>
                  <PasswordGenerateButton onGenerate={handleGeneratedPassword} />
                </div>
                {passwordError && formData.password.length > 0 && (
                  <>
                    <p className="password-requirements">{t('pwdRule')}</p>
                    <p className="password-requirements-special">{t('pwdRequirements')}</p>
                  </>
                )}
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={`password-toggle ${showPassword ? 'is-visible' : 'is-hidden'}`}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  />
                </div>
              </div>
              <div className="input-line">
                <label htmlFor="confirm-password">{t('confirmPassword')}</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirm-password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onKeyUp={handleConfirmPassword}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className={`password-toggle ${showConfirmPassword ? 'is-visible' : 'is-hidden'}`}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  />
                </div>
              </div>
              {pwdMatch.length > 0 ? 
                <div className='confirm-pwd-msg'>
                  {pwdMatch}
                </div> 
              : null }
            </div>

            <div className='input-line'>
              <div className="max-width">
                <AllTeamsMultiselect selectedValues={formData.teamIds} onChange={handleTeamChange}/>
              </div>
            </div> 
          </form>

          <div>
            {isLoading ? t('loading') : ''}
          </div>

            <button className="submit-button" type="submit" disabled={isLoading} onClick={handleSubmit}>{t('submit')}</button>

        </div>}
    </>
    ); 
};

export default CreateUser;
